import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { contentService } from "../services/contentService";
import SEO from "../components/SEO";

// Defaults used until the page is customized from the admin panel
export const TEAM_DEFAULTS = {
  h1: "Meet the Team",
  intro: [
    "ACF Luxury Stays is a family-run collection, and the people behind it are the reason every stay feels personal. We are not a call center or a remote management agency — we are the owners, and we are involved in every detail of your visit.",
    "From the first inquiry to the final checkout, you are working directly with the small team below. Each of us brings a different strength to the collection, but we share the same standard: your home should be pristine, welcoming, and looked after as if we were staying in it ourselves.",
  ],
  members: [],
};

const Team = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await contentService.getPageContent("team");
        if (data) setContent(data);
      } catch (error) {
        console.error("Failed to fetch team content:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const h1 = content?.h1 || TEAM_DEFAULTS.h1;
  const intro =
    Array.isArray(content?.intro) && content.intro.length > 0
      ? content.intro
      : TEAM_DEFAULTS.intro;
  const members = Array.isArray(content?.members) ? content.members : [];

  return (
    <div className="pt-32 pb-20">
      <SEO
        title="Meet the Team | ACF Luxury Stays"
        description="Meet the family team behind ACF Luxury Stays — the owners who personally oversee every home in our boutique collection."
        url="/team"
      />

      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-4xl md:text-5xl font-primary text-acf-blue mb-8">
            {h1}
          </h1>
          <div className="w-24 h-1 bg-acf-gold mx-auto mb-8"></div>

          <div className="max-w-3xl mx-auto mb-16">
            {intro.map((paragraph, idx) => (
              <p
                key={idx}
                className="font-secondary text-lg text-gray-600 leading-relaxed mb-6 last:mb-0"
              >
                {paragraph}
              </p>
            ))}
          </div>
        </div>

        {/* Team Members */}
        {loading ? (
          <p className="text-center font-secondary text-gray-400">
            Loading our team...
          </p>
        ) : members.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12 mb-16">
            {members.map((member, idx) => (
              <div key={member.id || idx} className="text-center">
                {member.photo?.url ? (
                  <img
                    src={member.photo.url}
                    alt={member.name || `Team member ${idx + 1}`}
                    className="w-full aspect-[4/5] object-cover rounded-sm shadow-md mb-6"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full aspect-[4/5] bg-acf-slate rounded-sm shadow-md mb-6 flex items-center justify-center">
                    <span className="font-primary text-acf-blue/40 text-5xl">
                      {(member.name || "?").charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}

                {member.name && (
                  <h2 className="font-primary text-acf-blue text-xl mb-1">
                    {member.name}
                  </h2>
                )}
                {member.role && (
                  <p className="font-secondary uppercase tracking-widest text-acf-gold text-xs mb-3">
                    {member.role}
                  </p>
                )}
                {member.bio && (
                  <p className="font-secondary text-gray-600 text-sm leading-relaxed">
                    {member.bio}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : null}

        <div className="text-center">
          <Link
            to="/contact"
            className="bg-acf-blue text-white px-8 py-3 font-primary uppercase tracking-widest hover:bg-acf-gold transition duration-300 no-underline"
          >
            Contact Our Team
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Team;
